/**
 * Live Lift Feed — Shows recent workouts from followed users + gym mates.
 *
 * Feed items come from the `posts` table (workout summaries, PRs)
 * filtered to followed users and same-gym users.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';
import { getFollowingIds } from './followSystem';
import { offlineActionQueue, isNetworkError } from './offlineSync';
import { isRateLimited, deduplicatedFetch } from './rateLimiter';

const log = createLogger('LIVE_FEED');

export interface FeedItem {
  id: string;
  userId: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  postType: 'workout' | 'pr' | 'challenge' | 'duel_result';
  content: string;
  caption: string | null;
  mediaUrl: string | null;
  gymId: string | null;
  createdAt: string;
  likeCount: number;
  commentCount: number;
}

export interface FeedOptions {
  userId: string;
  gymId?: string | null;
  limit?: number;
  before?: string; // cursor for pagination
}

/**
 * Fetch the live feed for a user.
 * Shows posts from: followed users + same-gym users.
 * Ordered by most recent first.
 */
export async function fetchLiveFeed(options: FeedOptions): Promise<FeedItem[]> {
  const { userId, gymId, limit = 20, before } = options;
  const dedupKey = `liveFeed:${userId}:${gymId}:${limit}:${before}`;

  return deduplicatedFetch(dedupKey, async () => {
  try {
    // Get IDs of users this person follows
    const followingIds = await getFollowingIds(userId);

    // Build the feed query
    // We want posts from: followed users OR same-gym users
    // But not the user's own posts (they see those on their profile)
    let query = supabase
      .from('posts')
      .select(`
        id,
        user_id,
        content,
        caption,
        post_type,
        media_url,
        gym_id,
        created_at,
        profiles!posts_user_id_fkey(id, handle, display_name, avatar_url)
      `)
      .neq('user_id', userId)
      .in('post_type', ['workout', 'pr'])
      .order('created_at', { ascending: false })
      .limit(limit);

    if (before) {
      query = query.lt('created_at', before);
    }

    // Filter: followed users OR same gym
    if (followingIds.length > 0 && gymId) {
      query = query.or(`user_id.in.(${followingIds.join(',')}),gym_id.eq.${gymId}`);
    } else if (followingIds.length > 0) {
      query = query.in('user_id', followingIds);
    } else if (gymId) {
      query = query.eq('gym_id', gymId);
    } else {
      // No follows and no gym — show nothing (or could show global recent)
      return [];
    }

    const { data, error } = await query;

    if (error) {
      log.error('fetchLiveFeed failed:', error);
      return [];
    }

    const mapped = (data ?? []).map((post: any) => ({
      id: post.id,
      userId: post.user_id,
      handle: post.profiles?.handle ?? 'unknown',
      displayName: post.profiles?.display_name ?? post.profiles?.handle ?? 'Lifter',
      avatarUrl: post.profiles?.avatar_url ?? null,
      postType: post.post_type,
      content: post.content ?? '',
      caption: post.caption,
      mediaUrl: post.media_url,
      gymId: post.gym_id,
      createdAt: post.created_at,
      likeCount: 0,
      commentCount: 0,
    }));

    return attachReactionCounts(mapped);
  } catch (err) {
    log.error('fetchLiveFeed exception:', err);
    return [];
  }
  });
}

/**
 * Fetch gym-only feed (all workouts at a specific gym).
 */
export async function fetchGymFeed(gymId: string, limit = 20, before?: string): Promise<FeedItem[]> {
  let query = supabase
    .from('posts')
    .select(`
      id,
      user_id,
      content,
      caption,
      post_type,
      media_url,
      gym_id,
      created_at,
      profiles!posts_user_id_fkey(id, handle, display_name, avatar_url)
    `)
    .eq('gym_id', gymId)
    .in('post_type', ['workout', 'pr'])
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;
  if (error) {
    log.error('fetchGymFeed failed:', error);
    return [];
  }

  const mapped = (data ?? []).map((post: any) => ({
    id: post.id,
    userId: post.user_id,
    handle: post.profiles?.handle ?? 'unknown',
    displayName: post.profiles?.display_name ?? post.profiles?.handle ?? 'Lifter',
    avatarUrl: post.profiles?.avatar_url ?? null,
    postType: post.post_type,
    content: post.content ?? '',
    caption: post.caption,
    mediaUrl: post.media_url,
    gymId: post.gym_id,
    createdAt: post.created_at,
    likeCount: 0,
    commentCount: 0,
  }));

  return attachReactionCounts(mapped);
}

// ── Reaction helpers ──────────────────────────────────────────────

/**
 * Batch-attach reaction counts to feed items.
 */
async function attachReactionCounts(items: FeedItem[]): Promise<FeedItem[]> {
  if (items.length === 0) return items;
  const postIds = items.map((i) => i.id);

  const { data, error } = await supabase
    .from('post_reactions')
    .select('post_id')
    .in('post_id', postIds);

  if (error) {
    log.error('attachReactionCounts failed:', error);
    return items;
  }

  const counts: Record<string, number> = {};
  for (const r of data ?? []) {
    counts[r.post_id] = (counts[r.post_id] || 0) + 1;
  }

  return items.map((item) => ({
    ...item,
    likeCount: counts[item.id] || 0,
  }));
}

/** Toggle a reaction on a post. Returns new count for that post. */
export async function toggleReaction(
  postId: string,
  userId: string,
  reactionType: 'fire' | 'strong' | 'respect' = 'fire'
): Promise<{ reacted: boolean; count: number }> {
  if (isRateLimited('reaction', 30, 60_000)) {
    throw new Error('Too many requests. Please wait a moment.');
  }
  try {
    // Check if already reacted
    const { data: existing } = await supabase
      .from('post_reactions')
      .select('id')
      .eq('post_id', postId)
      .eq('user_id', userId)
      .eq('reaction_type', reactionType)
      .maybeSingle();

    if (existing) {
      await supabase.from('post_reactions').delete().eq('id', existing.id);
    } else {
      await supabase
        .from('post_reactions')
        .insert({ post_id: postId, user_id: userId, reaction_type: reactionType });
    }

    // Get updated count
    const { count } = await supabase
      .from('post_reactions')
      .select('*', { count: 'exact', head: true })
      .eq('post_id', postId);

    return { reacted: !existing, count: count ?? 0 };
  } catch (err: any) {
    if (isNetworkError(err)) {
      await offlineActionQueue.enqueueAction('reaction', { postId, userId, reactionType });
      log.info('toggleReaction queued offline', { postId, userId });
      return { reacted: true, count: -1 };
    }
    throw err;
  }
}
